import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { ProfileAvatarProps } from './types';
import { cn } from '@/lib/utils';
import AvatarPics from '@/components/ui/AvatarPics';
import { ChevronDown } from 'lucide-react';

// Using forwardRef to properly handle refs from parent components
const ProfileAvatar = forwardRef<HTMLDivElement, ProfileAvatarProps>(({
  profilePictureUrl,
  initials,
  getFullName,
  onClick
}, ref) => {
  return (
    <motion.div 
      ref={ref}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={cn(
        "relative h-12 w-12 rounded-md overflow-hidden cursor-pointer",
        "ring-2 ring-white/20 dark:ring-slate-800/40",
        "shadow-md hover:shadow-lg",
        "transition-all duration-200"
      )}
    >
      {profilePictureUrl ? (
        <img 
          src={profilePictureUrl} 
          alt={getFullName()} 
          className="h-full w-full object-cover"
        />
      ) : (
        <div 
          className={cn(
            "h-full w-full flex items-center justify-center text-white text-sm font-medium",
            "bg-gradient-to-br from-brandPurple to-brandBlue dark:from-[#622bfe] dark:to-[#283dae]"
          )}
        >
          {initials}
        </div>
      )}

      {/* Subtle dropdown indicator */}
      <div className="absolute -bottom-0.5 -right-0.5 bg-white dark:bg-slate-800 rounded-full p-0.5 opacity-80">
        <ChevronDown size={10} className="text-brandPurple dark:text-brandBlue" />
      </div>
    </motion.div>
  );
});

// Add display name for better debugging
ProfileAvatar.displayName = 'ProfileAvatar';

export default ProfileAvatar;
